/**
 * Vue Composable for Thai Date and Time Management
 * Composable สำหรับจัดการวันที่และเวลาภาษาไทย
 */

import { ref, computed, onMounted, onUnmounted } from 'vue'
import {
  formatThaiDate,
  formatThaiDateShort,
  formatThaiDateTime,
  formatThaiDateTimeShort,
  formatThaiDateWithDay,
  formatThaiDateWithDayShort,
  formatThaiDateWithMonthAbbreviation,
  formatThaiMonthYear,
  formatTime24,
  formatTime24WithSeconds,
  formatRelativeDate,
  formatRelativeDateTime,
  formatTimeRange,
  formatThaiDateRange,
  formatThaiDateRangeShort,
  formatDuration,
  isToday,
  isYesterday,
  isTomorrow,
  isWeekend,
  now,
  today,
  tomorrow,
  yesterday,
  toBuddhistYear,
  THAI_MONTHS,
  THAI_MONTHS_SHORT,
  THAI_DAYS,
  THAI_DAYS_SHORT
} from '../utils/dateTime'

export function useDateTime() {
  // Reactive current time
  const currentTime = ref<Date>(now())
  let timeInterval: ReturnType<typeof setInterval> | null = null

  // Start real-time clock
  const startClock = (intervalMs = 1000) => {
    if (timeInterval) {
      clearInterval(timeInterval)
    }
    
    timeInterval = setInterval(() => {
      currentTime.value = now()
    }, intervalMs)
  }

  // Stop real-time clock
  const stopClock = () => {
    if (timeInterval) {
      clearInterval(timeInterval)
      timeInterval = null
    }
  }

  // Computed properties for current time
  const currentThaiDate = computed(() => formatThaiDate(currentTime.value))
  const currentThaiDateShort = computed(() => formatThaiDateShort(currentTime.value))
  const currentThaiDateTime = computed(() => formatThaiDateTime(currentTime.value))
  const currentThaiDateTimeShort = computed(() => formatThaiDateTimeShort(currentTime.value))
  const currentTime24 = computed(() => formatTime24(currentTime.value))
  const currentTime24WithSeconds = computed(() => formatTime24WithSeconds(currentTime.value))
  const currentThaiDateWithDay = computed(() => formatThaiDateWithDay(currentTime.value))
  const currentBuddhistYear = computed(() => toBuddhistYear(currentTime.value.getFullYear()))

  // Lifecycle hooks
  onMounted(() => {
    startClock()
  })

  onUnmounted(() => {
    stopClock()
  })

  // Format functions
  const formatDate = (date: Date | string | null, format: 'full' | 'short' | 'withDay' | 'withDayShort' | 'monthYear' | 'monthAbbreviation' = 'full'): string => {
    // DEBUG: Log format function calls
    console.log('DEBUG: useDateTime formatDate - Input date:', date)
    console.log('DEBUG: useDateTime formatDate - Requested format:', format)
    
    if (!date) return '-'
    
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return '-'

    let result: string
    switch (format) {
      case 'short':
        result = formatThaiDateShort(dateObj)
        break
      case 'withDay':
        result = formatThaiDateWithDay(dateObj)
        break
      case 'withDayShort':
        result = formatThaiDateWithDayShort(dateObj)
        break
      case 'monthYear':
        result = formatThaiMonthYear(dateObj)
        break
      case 'monthAbbreviation':
        result = formatThaiDateWithMonthAbbreviation(dateObj)
        break
      default:
        result = formatThaiDate(dateObj)
        break
    }
    
    console.log('DEBUG: useDateTime formatDate - Output:', result)
    return result
  }

  const formatDateTime = (date: Date | string | null, format: 'full' | 'short' | 'relative' = 'full'): string => {
    if (!date) return '-'
    
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return '-'

    switch (format) {
      case 'short':
        return formatThaiDateTimeShort(dateObj)
      case 'relative':
        return formatRelativeDateTime(dateObj)
      default:
        return formatThaiDateTime(dateObj)
    }
  }

  const formatTime = (date: Date | string | null, withSeconds = false): string => {
    if (!date) return '-'
    
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return '-'

    return withSeconds ? formatTime24WithSeconds(dateObj) : formatTime24(dateObj)
  }

  const formatDateRange = (startDate: Date | string | null, endDate: Date | string | null): string => {
    if (!startDate || !endDate) return '-'
    
    const startDateObj = typeof startDate === 'string' ? new Date(startDate) : startDate
    const endDateObj = typeof endDate === 'string' ? new Date(endDate) : endDate
    
    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) return '-'

    return formatThaiDateRange(startDateObj, endDateObj)
  }

  const formatDateRangeShort = (startDate: Date | string | null, endDate: Date | string | null): string => {
    if (!startDate || !endDate) return '-'
    
    const startDateObj = typeof startDate === 'string' ? new Date(startDate) : startDate
    const endDateObj = typeof endDate === 'string' ? new Date(endDate) : endDate
    
    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) return '-'

    return formatThaiDateRangeShort(startDateObj, endDateObj)
  }

  const getRelativeDate = (date: Date | string | null): string => {
    if (!date) return '-'
    
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return '-'

    return formatRelativeDate(dateObj)
  }

  const getRelativeDateTime = (date: Date | string | null): string => {
    if (!date) return '-'
    
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return '-'

    return formatRelativeDateTime(dateObj)
  }

  // Utility functions
  const isDateToday = (date: Date | string | null): boolean => {
    if (!date) return false
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return isToday(dateObj)
  }

  const isDateYesterday = (date: Date | string | null): boolean => {
    if (!date) return false
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return isYesterday(dateObj)
  }

  const isDateTomorrow = (date: Date | string | null): boolean => {
    if (!date) return false
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return isTomorrow(dateObj)
  }

  const isDateWeekend = (date: Date | string | null): boolean => {
    if (!date) return false
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return isWeekend(dateObj)
  }

  // Date creation helpers
  const createToday = () => today()
  const createTomorrow = () => tomorrow()
  const createYesterday = () => yesterday()
  const createNow = () => now()

  // Time range formatting
  const formatScheduleTime = (startTime: string, endTime: string): string => {
    return formatTimeRange(startTime, endTime)
  }

  // Duration formatting
  const formatMinutesToDuration = (minutes: number): string => {
    return formatDuration(minutes)
  }

  // Get month and day names
  const getThaiMonthName = (monthIndex: number, short = false): string => {
    const months = short ? THAI_MONTHS_SHORT : THAI_MONTHS
    return months[monthIndex] || ''
  }

  const getThaiDayName = (dayIndex: number, short = false): string => {
    const days = short ? THAI_DAYS_SHORT : THAI_DAYS
    return days[dayIndex] || ''
  }

  // Date validation
  const isValidDate = (date: Date | string | null): boolean => {
    if (!date) return false
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return !isNaN(dateObj.getTime())
  }

  // Parse date string to Date object
  const parseDate = (dateString: string): Date | null => {
    try {
      const date = new Date(dateString)
      return isNaN(date.getTime()) ? null : date
    } catch {
      return null
    }
  }

  // Format for input fields
  const formatForInput = (date: Date | string | null, type: 'date' | 'datetime-local' | 'time' = 'date'): string => {
    if (!date) return ''
    
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return ''

    switch (type) {
      case 'datetime-local':
        return dateObj.toISOString().slice(0, 16)
      case 'time':
        return dateObj.toTimeString().slice(0, 5)
      default:
        return dateObj.toISOString().slice(0, 10)
    }
  }

  // Get current date/time in various formats
  const getCurrentDate = () => ({
    date: currentTime.value,
    thaiDate: currentThaiDate.value,
    thaiDateShort: currentThaiDateShort.value,
    thaiDateTime: currentThaiDateTime.value,
    thaiDateTimeShort: currentThaiDateTimeShort.value,
    time24: currentTime24.value,
    time24WithSeconds: currentTime24WithSeconds.value,
    thaiDateWithDay: currentThaiDateWithDay.value,
    buddhistYear: currentBuddhistYear.value
  })

  return {
    // Reactive current time
    currentTime,
    currentThaiDate,
    currentThaiDateShort,
    currentThaiDateTime,
    currentThaiDateTimeShort,
    currentTime24,
    currentTime24WithSeconds,
    currentThaiDateWithDay,
    currentBuddhistYear,

    // Clock control
    startClock,
    stopClock,

    // Formatting functions
    formatDate,
    formatDateTime,
    formatTime,
    formatDateRange,
    formatDateRangeShort,
    formatScheduleTime,
    formatMinutesToDuration,
    getRelativeDate,
    getRelativeDateTime,

    // Utility functions
    isDateToday,
    isDateYesterday,
    isDateTomorrow,
    isDateWeekend,
    isValidDate,
    parseDate,
    formatForInput,

    // Date creation
    createToday,
    createTomorrow,
    createYesterday,
    createNow,

    // Name getters
    getThaiMonthName,
    getThaiDayName,

    // Current date getter
    getCurrentDate,

    // Constants
    THAI_MONTHS,
    THAI_MONTHS_SHORT,
    THAI_DAYS,
    THAI_DAYS_SHORT
  }
}

// Global instance for app-wide usage
let globalDateTimeInstance: ReturnType<typeof useDateTime> | null = null

export const useGlobalDateTime = () => {
  if (!globalDateTimeInstance) {
    globalDateTimeInstance = useDateTime()
  }
  return globalDateTimeInstance
}